<template>
  <div class="modal fade" tabindex="-1" data-backdrop="static">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <button class="close" type="button" @click="cancel">
            <span>&times;</span>
          </button>
          <h4 class="modal-title">Advanced Editing Options</h4>
          <p class="text-muted" style="margin-top: 6px; font-size: 15px; font-weight: normal;">
            Changes will be applied to {{ storyCountLabel }}. Only sections with a value selected will be updated.
          </p>
        </div>

        <div class="modal-body">
          <div class="advanced-batch-section" v-if="publicationsStore.hasMultiple">
            <div class="flex-between">
              <h5 class="advanced-batch-section__title">Publication Assignment</h5>
              <HelpPopover title="Publication Assignment" :content="publicationHelp" />
            </div>
            <div class="row">
              <div class="col-sm-8">
                <div class="form-group">
                  <label>
                    Publications
                    <small class="text-muted" style="font-weight: normal; margin-left: 6px;">Hold Ctrl/Cmd to select multiple</small>
                  </label>
                  <select
                    class="form-control"
                    multiple
                    size="4"
                    v-model="storyBatchOptionsStore.advancedPublicationIds"
                  >
                    <option
                      v-for="pub in publicationOptions"
                      :key="pub.value"
                      :value="pub.value"
                    >{{ pub.label }}</option>
                  </select>
                </div>
              </div>
              <div class="col-sm-4">
                <div class="form-group">
                  <label>Existing setting</label>
                  <br>
                  <RadioGroup
                    name="publication-mode"
                    v-model="storyBatchOptionsStore.advancedPublicationMode"
                    :options="appendReplaceModeOptions"
                  />
                </div>
              </div>
            </div>
          </div>

          <hr v-if="publicationsStore.hasMultiple">

          <div class="advanced-batch-section">
            <div class="flex-between">
              <h5 class="advanced-batch-section__title">Issue Assignment</h5>
              <HelpPopover title="Issue Assignment" :content="issueAssignmentHelp" />
            </div>
            <div class="row">
              <div class="col-sm-8">
                <div class="form-group">
                  <label>
                    Issues
                    <small class="text-muted" style="font-weight: normal; margin-left: 6px;">Hold Ctrl/Cmd to select multiple</small>
                  </label>
                  <select
                    class="form-control"
                    multiple
                    size="6"
                    v-model="storyBatchOptionsStore.advancedIssueIds"
                  >
                    <option
                      v-for="issue in filteredIssues"
                      :key="issue.value"
                      :value="issue.value"
                    >{{ issue.label }}</option>
                  </select>
                </div>
              </div>
              <div class="col-sm-4">
                <div class="form-group">
                  <label>Existing setting</label>
                  <br>
                  <RadioGroup
                    name="issue-assignment-mode"
                    v-model="storyBatchOptionsStore.advancedIssueAssignmentMode"
                    :options="appendReplaceModeOptions"
                  />
                </div>
              </div>
            </div>
          </div>

          <hr>

          <div class="advanced-batch-section" v-if="filteredSubsites.length > 1">
            <div class="flex-between">
              <h5 class="advanced-batch-section__title">Subsite Assignment</h5>
              <HelpPopover title="Subsite Assignment" :content="subsiteAssignmentHelp" />
            </div>
            <div class="row">
              <div class="col-sm-8">
                <div class="form-group">
                  <label>
                    Subsites
                    <small class="text-muted" style="font-weight: normal; margin-left: 6px;">Hold Ctrl/Cmd to select multiple</small>
                  </label>
                  <select
                    class="form-control"
                    multiple
                    size="6"
                    v-model="storyBatchOptionsStore.advancedSubsiteIds"
                  >
                    <option
                      v-for="subsite in filteredSubsites"
                      :key="subsite.value"
                      :value="subsite.value"
                    >{{ subsite.label }}</option>
                  </select>
                </div>
              </div>
              <div class="col-sm-4">
                <div class="form-group">
                  <label>Existing setting</label>
                  <br>
                  <RadioGroup
                    name="subsite-assignment-mode"
                    v-model="storyBatchOptionsStore.advancedSubsiteMode"
                    :options="appendReplaceModeOptions"
                  />
                </div>
              </div>
            </div>
          </div>

          <hr>

          <div class="advanced-batch-section">
            <div class="flex-between">
              <h5 class="advanced-batch-section__title">Story Flags</h5>
              <HelpPopover title="Story Flags" :content="storyFlagsHelp" />
            </div>
            <div class="row">
              <div class="col-sm-3">
                <div class="form-group">
                  <label>Featured</label>
                  <SelectInput
                    v-model="storyBatchOptionsStore.advancedFeatured"
                    blank-option-name="Don't Change"
                    :options="{ 'Yes': '1', 'No': '0' }"
                  />
                </div>
              </div>
              <div class="col-sm-3">
                <div class="form-group">
                  <label>Free</label>
                  <SelectInput
                    v-model="storyBatchOptionsStore.advancedIspublic"
                    blank-option-name="Don't Change"
                    :options="{ 'Yes': '1', 'No': '0' }"
                  />
                </div>
              </div>
            </div>
          </div>

          <hr>

          <div class="advanced-batch-section">
            <div class="flex-between">
              <h5 class="advanced-batch-section__title">Keywords</h5>
              <HelpPopover title="Keywords" :content="keywordsHelp" />
            </div>
            <div class="row">
              <div class="col-sm-8">
                <div class="form-group">
                  <label>Keywords</label>
                  <input
                    class="form-control"
                    type="text"
                    placeholder="Comma-separated keywords"
                    v-model="storyBatchOptionsStore.advancedKeywords"
                  >
                </div>
              </div>
              <div class="col-sm-4">
                <div class="form-group">
                  <label>Existing setting</label>
                  <br>
                  <RadioGroup
                    name="keywords-mode"
                    v-model="storyBatchOptionsStore.advancedKeywordsMode"
                    :options="appendReplaceModeOptions"
                  />
                </div>
              </div>
            </div>
          </div>

          <hr>

          <div class="advanced-batch-section">
            <div class="flex-between">
              <h5 class="advanced-batch-section__title">Notes</h5>
              <HelpPopover title="Notes" :content="notesHelp" />
            </div>
            <div class="row">
              <div class="col-sm-8">
                <div class="form-group">
                  <label>Note text</label>
                  <textarea
                    class="form-control"
                    rows="4"
                    placeholder="Add a note to the selected stories..."
                    v-model="storyBatchOptionsStore.advancedNotes"
                  ></textarea>
                </div>
              </div>
              <div class="col-sm-4">
                <div class="form-group">
                  <label>Existing setting</label>
                  <br>
                  <RadioGroup
                    name="notes-mode"
                    v-model="storyBatchOptionsStore.advancedNotesMode"
                    :options="appendReplaceModeOptions"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-default" type="button" @click="cancel">
            Cancel
          </button>
          <button
            class="btn btn-primary"
            type="button"
            :disabled="!storyBatchOptionsStore.anyAdvancedChanges"
            @click="submit"
          >
            Apply Advanced Changes to {{ storyCountLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapStores } from 'pinia'
  import { useStoryBatchOptionsStore } from '@/stores/story_batch_options'
  import { useIssuesStore } from '@/stores/issues'
  import { usePublicationsStore } from '@/stores/publications'
  import { useSubsitesStore } from '@/stores/subsites'
  import HelpPopover from '@/components/shared/help_popover.vue'
  import RadioGroup from '@/components/shared/radio_group.vue'
  import httpClient from '@/api/httpClient'

  export default {
    components: {
      HelpPopover,
      RadioGroup
    },

    props: {
      visible: {
        type: Boolean,
        required: true
      }
    },

    data() {
      return {
        appendReplaceModeOptions: [
          { label: 'Append', value: 'append' },
          { label: 'Replace', value: 'replace' }
        ],

        publicationHelp: `Assign the selected stories to one or more publications. This controls which publications the stories belong to, and determines which subsites are available for assignment below.

Use "Append" to add the selected publications while keeping any existing publication assignments. Use "Replace" to remove existing publication assignments and set only the ones selected here.

Note: Issues and subsites below are filtered to show only those belonging to the selected publications.`,

        issueAssignmentHelp: `Assign the selected stories to one or more issues. Issues represent upcoming editions of your publication and help organize stories for print production.

Use "Append" to add the selected issues while keeping any existing issue assignments. Use "Replace" to remove existing issue assignments and set only the ones selected here.`,

        subsiteAssignmentHelp: `Assign the selected stories to one or more subsites. Subsites allow you to publish content to different sections or editions of your website.

Use "Append" to add the selected subsites while keeping any existing subsite assignments. Use "Replace" to remove existing assignments and set only the ones selected here.`,

        storyFlagsHelp: `Toggle flags on the selected stories.

Featured marks stories as featured content, which may give them prominent placement on your website.

Free marks stories as free content that does not require a subscription or paywall access to read.`,

        keywordsHelp: `Add keywords to help readers and search engines find the selected stories. Separate multiple keywords with commas.

Use "Append" to add new keywords while keeping any existing ones. Use "Replace" to clear existing keywords and set only the ones entered here.`,

        notesHelp: `Add internal notes to the selected stories. Notes are for editorial use only and are never published.

Use "Append" to add the note text to any existing notes. Use "Replace" to clear existing notes and set only the text entered here.`
      }
    },

    computed: {
      ...mapStores(
        useStoryBatchOptionsStore,
        useIssuesStore,
        usePublicationsStore,
        useSubsitesStore
      ),

      publicationOptions() {
        return this.publicationsStore.publications.map(pub => ({
          label: pub.name,
          value: pub.id
        }))
      },

      selectedPublicationIds() {
        return this.storyBatchOptionsStore.advancedPublicationIds
      },

      filteredIssues() {
        const pubIds = this.selectedPublicationIds
        if (pubIds.length === 0) return this.issuesStore.allAsArray

        return pubIds.flatMap(pubId =>
          this.issuesStore.forPublication({ id: pubId }).map(issue => ({
            label: `${issue.publication.name}: ${issue.title} (${issue.publish_on})`,
            value: issue.id
          }))
        )
      },

      filteredSubsites() {
        const pubIds = this.selectedPublicationIds
        if (pubIds.length === 0) return this.subsitesStore.allAsArray

        return pubIds.flatMap(pubId =>
          this.subsitesStore.forPublication({ publicationId: pubId }).map(sub => ({
            label: sub.name,
            value: sub.id
          }))
        )
      },

      storyCountLabel() {
        const count = this.storyBatchOptionsStore.selectedStoriesCount
        switch (count) {
          case 1: return '1 Story'
          case 2: return 'Both Stories'
          default: return `All ${count} Stories`
        }
      }
    },

    watch: {
      async visible(val) {
        if (val) {
          await this.publicationsStore.fetchOnce()
          this.issuesStore.fetchAllOnce()
          this.subsitesStore.fetchOnce()
          jQuery(this.$el).modal('show')
        } else {
          jQuery(this.$el).modal('hide')
        }
      },

      selectedPublicationIds() {
        // Clear issue/subsite selections when publication selection changes,
        // since the filtered lists may no longer contain the previously selected items
        this.storyBatchOptionsStore.advancedIssueIds = []
        this.storyBatchOptionsStore.advancedSubsiteIds = []
      }
    },

    mounted() {
      jQuery(this.$el).on('hidden.bs.modal', () => {
        this.$emit('close')
      })
    },

    beforeDestroy() {
      jQuery(this.$el).off('hidden.bs.modal')
    },

    methods: {
      cancel() {
        this.storyBatchOptionsStore.resetAdvanced()
        this.$emit('close')
      },

      submit() {
        if (!this.storyBatchOptionsStore.anyAdvancedChanges) return

        const data = {
          ids: this.storyBatchOptionsStore.selectedStoryIds,
          story: this.storyBatchOptionsStore.advancedStoryData,
          batch: this.storyBatchOptionsStore.advancedBatchData
        }

        httpClient.patch('/api/internal/stories/batch_update', data).then(() => {
          this.$store.dispatch('messages/addNotice', `Editing changes applied to ${this.storyCountLabel.toLowerCase()}`)
          this.storyBatchOptionsStore.resetAdvanced()
          this.$emit('close')
          this.$emit('change')
        }).catch(err => {
          this.$store.dispatch('messages/addError', err)
        })
      }
    }
  }
</script>

<style scoped>
  .advanced-batch-section__title {
    font-weight: bold;
    color: #555;
    margin-bottom: 12px;
    text-transform: uppercase;
    font-size: 13px;
    letter-spacing: 0.5px;
  }

  .advanced-batch-section {
    padding: 4px 0;
    font-size: 15px;
  }

  .advanced-batch-section label {
    font-size: 15px;
  }

  .advanced-batch-section .form-control {
    font-size: 15px;
  }

  .modal-header .modal-title {
    font-size: 20px;
  }

  .modal-footer .btn {
    font-size: 15px;
  }
</style>
